// Interactive scenarios guide — the Interactive Plugin (Beta) under
// Components → Interactive: a full-scene branching scenario edited in Design
// mode (scene tabs + the right-rail Elements panel) and tested in Simulator
// mode (InteractivePlugin, EditorApp interactive panel). Locale-keyed like
// every guide (content.<lang> → { feature, title, sections }).

const interactiveScenariosGuide = {
  id: "interactive-scenarios",
  icon: "call_split",
  keywords: [
    "interactive",
    "etkileşim",
    "interaktif",
    "interactive plugin",
    "etkileşim eklentisi",
    "scenario",
    "senaryo",
    "branching",
    "dallanma",
    "branch",
    "dal",
    "simulator",
    "simülatör",
    "design",
    "tasarım",
    "flip card",
    "çift yüzlü kart",
    "true false",
    "doğru yanlış",
    "multiple choice",
    "çoktan seçmeli",
    "action",
    "aksiyon",
    "go to scene",
    "sahneye git",
    "end scenario",
    "senaryoyu bitir",
    "beta",
    "interactivo",
    "escenario",
    "ramificación",
    "simulador",
    "diseño",
    "tarjeta reversible",
    "verdadero falso",
    "opción múltiple",
    "acción",
    "ir a la escena",
    "finalizar escenario",
  ],
  content: {
    en: {
      feature: "Components",
      title: "Interactive Scenarios (Beta)",
      sections: [
        {
          id: "overview",
          title: "Adding an interactive scenario",
          videoUrl: null,
          text:
            "An interactive scenario is a small branching experience inside one scene: the learner answers questions or flips cards, and each answer can send them to a different part of the scenario. This feature is in **Beta**.\n\n" +
            "- Open **Components → Interactive** and click or drag **Interactive Plugin** (marked **Beta**) onto a scene. It fills the whole scene.\n" +
            "- A scenario has its own scenes, separate from your training's scenes. A new one starts with a single **Start** scene.\n" +
            "- When the element isn't selected it shows a summary card (for example **New Scenario · 1 scenes**). Double-click it or click **Edit** to start designing.",
        },
        {
          id: "design-mode",
          title: "Designing scenes",
          videoUrl: null,
          text:
            "With the scenario selected, its scenes appear as tabs across the top of the element — the start scene is marked with ▶. Click a tab to open that scene, **+** to add a scene, and the trash icon to delete the open scene (the start scene can't be deleted).\n\n" +
            "Open the **Elements** tab in the right-hand icon strip to get the **Senaryo** (Scenario) panel:\n\n" +
            "- **Design** / **Simulator** — switch between editing and trying the scenario.\n" +
            "- **Sahneler** (Scenes) — the scene list; **+ Sahne** adds a scene.\n" +
            "- **Arka Plan** (Background) — pick **Transparent**, **Dark**, **Navy**, **White**, **Gray** or a custom color, and set the scene's **Metin rengi** (text color).\n" +
            "- **Elementler** (Elements) — add a **Flip Card**, **True / False** or **Multiple Choice** element. It appears on the scene, where you can drag and resize it; the trash icon in the list removes it.",
        },
        {
          id: "element-properties",
          title: "Element properties",
          videoUrl: null,
          text:
            "Select an element on the scene or in the list to edit it under **Properties**. Every element has a title and a **Starts visible** / **Starts hidden** toggle — a hidden element can be revealed later by an action.\n\n" +
            "- **Flip Card** — a **Front Face** and **Back Face**, each with a title, text, optional image and colors. **Action triggered on flip** runs when the learner flips the card; **Action button (optional)** adds a button on the back with its own actions.\n" +
            "- **True / False** — the question, the two button labels and the **Correct answer**. **Correct Actions** run after a right answer, **Incorrect Actions** after a wrong one; you can add an explanation.\n" +
            "- **Multiple Choice** — the question and its choices (**+ Add Choice**). Each choice has its own actions, so every answer can lead somewhere different.",
        },
        {
          id: "actions",
          title: "Actions and branching",
          videoUrl: null,
          text:
            "Actions decide what happens after the learner answers or flips. Click **+ Add Action** under an element's action list and choose a type:\n\n" +
            "- **Go to Scene** — moves the learner to another scenario scene. This is how you build branches.\n" +
            "- **Show/Hide Element** — shows, hides or toggles an element on the current scene.\n" +
            "- **Notification** — shows a message the learner closes with **OK**.\n" +
            "- **End Scenario** — finishes the scenario.\n\n" +
            "Actions run from top to bottom. **Go to Scene** and **End Scenario** stop the list, so put notifications and show/hide actions before them.",
        },
        {
          id: "simulator",
          title: "Testing with the Simulator",
          videoUrl: null,
          text:
            "Switch to **Simulator** in the panel to play the scenario from the start scene right on the canvas, exactly as learners will see it. Switch back to **Design** to keep editing.\n\n" +
            "When the scenario ends, learners see **Interactive scenario completed** with a **Restart** button. As a Beta feature, check your scenario in the Simulator and in the course preview before publishing.",
        },
      ],
    },
    tr: {
      feature: "Bileşenler",
      title: "Etkileşimli Senaryolar (Beta)",
      sections: [
        {
          id: "overview",
          title: "Etkileşimli senaryo ekleme",
          videoUrl: null,
          text:
            "Etkileşimli senaryo, tek bir sahnenin içinde yer alan küçük, dallanan bir deneyimdir: öğrenci soruları cevaplar veya kartları çevirir ve her cevap onu senaryonun farklı bir bölümüne götürebilir. Bu özellik **Beta** aşamasındadır.\n\n" +
            "- **Bileşenler → Etkileşim**'i açın ve **Etkileşim Eklentisi**'ni (**Beta** etiketli) bir sahneye tıklayarak veya sürükleyerek ekleyin. Tüm sahneyi kaplar.\n" +
            "- Senaryonun eğitiminizin sahnelerinden ayrı, kendi sahneleri vardır. Yeni bir senaryo tek bir **Başlangıç** sahnesiyle başlar.\n" +
            "- Öğe seçili değilken bir özet kartı gösterir (örneğin **Yeni Senaryo · 1 sahne**). Tasarlamaya başlamak için karta çift tıklayın veya **Düzenle**'ye tıklayın.",
        },
        {
          id: "design-mode",
          title: "Sahneleri tasarlama",
          videoUrl: null,
          text:
            "Senaryo seçiliyken sahneleri öğenin üst kısmında sekmeler olarak görünür — başlangıç sahnesi ▶ ile işaretlidir. Bir sahneyi açmak için sekmesine, sahne eklemek için **+** simgesine, açık sahneyi silmek için çöp kutusu simgesine tıklayın (başlangıç sahnesi silinemez).\n\n" +
            "**Senaryo** panelini açmak için sağdaki simge şeridinden **Öğeler** sekmesini açın:\n\n" +
            "- **Tasarım** / **Simülatör** — düzenleme ile senaryoyu deneme arasında geçiş yapar.\n" +
            "- **Sahneler** — sahne listesi; **+ Sahne** yeni bir sahne ekler.\n" +
            "- **Arka Plan** — **Şeffaf**, **Koyu**, **Lacivert**, **Beyaz**, **Gri** ya da özel bir renk seçin ve sahnenin **Metin rengi**'ni belirleyin.\n" +
            "- **Elementler** — **Çift Yüzlü Kart**, **Doğru / Yanlış** veya **Çoktan Seçmeli** öğe ekleyin. Öğe sahnede belirir; orada sürükleyip boyutlandırabilirsiniz. Listedeki çöp kutusu simgesi onu kaldırır.",
        },
        {
          id: "element-properties",
          title: "Öğe özellikleri",
          videoUrl: null,
          text:
            "Düzenlemek için sahnede veya listede bir öğe seçin; ayarları **Özellikler** altında görünür. Her öğenin bir başlığı ve **Görünür başlar** / **Gizli başlar** anahtarı vardır — gizli bir öğe daha sonra bir aksiyonla gösterilebilir.\n\n" +
            "- **Çift Yüzlü Kart** — her biri başlık, metin, isteğe bağlı görsel ve renkler içeren bir **Ön Yüz** ve **Arka Yüz**. **Çevirince tetiklenen aksiyon**, öğrenci kartı çevirdiğinde çalışır; **Aksiyonlu buton (isteğe bağlı)** arka yüze kendi aksiyonları olan bir buton ekler.\n" +
            "- **Doğru / Yanlış** — soru, iki buton etiketi ve **Doğru cevap**. **Doğru Aksiyonlar** doğru cevaptan, **Yanlış Aksiyonlar** yanlış cevaptan sonra çalışır; bir açıklama da ekleyebilirsiniz.\n" +
            "- **Çoktan Seçmeli** — soru ve seçenekleri (**+ Seçenek Ekle**). Her seçeneğin kendi aksiyonları vardır; böylece her cevap farklı bir yere götürebilir.",
        },
        {
          id: "actions",
          title: "Aksiyonlar ve dallanma",
          videoUrl: null,
          text:
            "Aksiyonlar, öğrenci cevap verdikten veya kartı çevirdikten sonra ne olacağını belirler. Bir öğenin aksiyon listesinin altındaki **+ Aksiyon Ekle**'ye tıklayın ve bir tür seçin:\n\n" +
            "- **Sahneye Git** — öğrenciyi senaryonun başka bir sahnesine götürür. Dallanmalar bu şekilde kurulur.\n" +
            "- **Element Göster/Gizle** — geçerli sahnedeki bir öğeyi gösterir, gizler veya durumunu değiştirir.\n" +
            "- **Bildirim** — öğrencinin **Tamam** ile kapattığı bir mesaj gösterir.\n" +
            "- **Senaryoyu Bitir** — senaryoyu sonlandırır.\n\n" +
            "Aksiyonlar yukarıdan aşağıya çalışır. **Sahneye Git** ve **Senaryoyu Bitir** listeyi durdurur; bu yüzden bildirimleri ve göster/gizle aksiyonlarını bunlardan önce yerleştirin.",
        },
        {
          id: "simulator",
          title: "Simülatörle test etme",
          videoUrl: null,
          text:
            "Senaryoyu başlangıç sahnesinden itibaren doğrudan tuval üzerinde, öğrencilerin göreceği şekilde oynatmak için paneldeki **Simülatör**'e geçin. Düzenlemeye devam etmek için **Tasarım**'a dönün.\n\n" +
            "Senaryo bittiğinde öğrenciler **İnteraktif tamamlandı** mesajını ve **Baştan Başla** düğmesini görür. Beta bir özellik olduğu için yayınlamadan önce senaryonuzu Simülatörde ve eğitim önizlemesinde kontrol edin.",
        },
      ],
    },
    es: {
      feature: "Componentes",
      title: "Escenarios interactivos (Beta)",
      sections: [
        {
          id: "overview",
          title: "Añadir un escenario interactivo",
          videoUrl: null,
          text:
            "Un escenario interactivo es una pequeña experiencia ramificada dentro de una escena: el alumno responde preguntas o voltea tarjetas, y cada respuesta puede llevarlo a una parte distinta del escenario. Esta función está en **Beta**.\n\n" +
            "- Abre **Componentes → Interactivo** y haz clic en **Plugin Interactivo** (marcado como **Beta**) o arrástralo a una escena. Ocupa toda la escena.\n" +
            "- Un escenario tiene sus propias escenas, independientes de las escenas de tu formación. Uno nuevo empieza con una sola escena **Inicio**.\n" +
            "- Cuando el elemento no está seleccionado, muestra una tarjeta de resumen (por ejemplo, **Nuevo Escenario · 1 escenas**). Haz doble clic en ella o haz clic en **Editar** para empezar a diseñar.",
        },
        {
          id: "design-mode",
          title: "Diseñar escenas",
          videoUrl: null,
          text:
            "Con el escenario seleccionado, sus escenas aparecen como pestañas en la parte superior del elemento; la escena de inicio está marcada con ▶. Haz clic en una pestaña para abrir esa escena, en **+** para añadir una escena y en el icono de la papelera para eliminar la escena abierta (la escena de inicio no se puede eliminar).\n\n" +
            "Abre la pestaña **Elementos** en la franja de iconos de la derecha para ver el panel **Senaryo** (Escenario):\n\n" +
            "- **Diseño** / **Simulador**: cambia entre editar y probar el escenario.\n" +
            "- **Sahneler** (Escenas): la lista de escenas; **+ Sahne** añade una escena.\n" +
            "- **Arka Plan** (Fondo): elige **Transparente**, **Oscuro**, **Azul marino**, **Blanco**, **Gris** o un color personalizado, y define el **Metin rengi** (color del texto) de la escena.\n" +
            "- **Elementler** (Elementos): añade un elemento **Tarjeta reversible**, **Verdadero / Falso** u **Opción múltiple**. Aparece en la escena, donde puedes arrastrarlo y cambiar su tamaño; el icono de la papelera de la lista lo elimina.",
        },
        {
          id: "element-properties",
          title: "Propiedades de los elementos",
          videoUrl: null,
          text:
            "Selecciona un elemento en la escena o en la lista para editarlo en **Propiedades**. Todos los elementos tienen un título y un interruptor **Inicia visible** / **Inicia oculto**; un elemento oculto puede mostrarse más tarde mediante una acción.\n\n" +
            "- **Tarjeta reversible**: una **Cara frontal** y una **Cara trasera**, cada una con título, texto, imagen opcional y colores. **Acción al voltear** se ejecuta cuando el alumno voltea la tarjeta; **Botón de acción (opcional)** añade un botón en la cara trasera con sus propias acciones.\n" +
            "- **Verdadero / Falso**: la pregunta, las etiquetas de los dos botones y la **Respuesta correcta**. Las **Acciones Correctas** se ejecutan tras una respuesta correcta y las **Acciones Incorrectas**, tras una incorrecta; también puedes añadir una explicación.\n" +
            "- **Opción múltiple**: la pregunta y sus opciones (**+ Agregar opción**). Cada opción tiene sus propias acciones, así que cada respuesta puede llevar a un lugar distinto.",
        },
        {
          id: "actions",
          title: "Acciones y ramificación",
          videoUrl: null,
          text:
            "Las acciones deciden qué ocurre después de que el alumno responde o voltea una tarjeta. Haz clic en **+ Agregar acción** bajo la lista de acciones de un elemento y elige un tipo:\n\n" +
            "- **Ir a la escena**: lleva al alumno a otra escena del escenario. Así es como se crean las ramas.\n" +
            "- **Mostrar/Ocultar elemento**: muestra, oculta o alterna un elemento de la escena actual.\n" +
            "- **Notificación**: muestra un mensaje que el alumno cierra con **Aceptar**.\n" +
            "- **Finalizar escenario**: termina el escenario.\n\n" +
            "Las acciones se ejecutan de arriba abajo. **Ir a la escena** y **Finalizar escenario** detienen la lista, así que coloca las notificaciones y las acciones de mostrar/ocultar antes que ellas.",
        },
        {
          id: "simulator",
          title: "Probar con el Simulador",
          videoUrl: null,
          text:
            "Cambia a **Simulador** en el panel para reproducir el escenario desde la escena de inicio directamente en el lienzo, tal como lo verán los alumnos. Vuelve a **Diseño** para seguir editando.\n\n" +
            "Cuando el escenario termina, los alumnos ven **Escenario interactivo completado** con un botón **Reiniciar**. Al tratarse de una función Beta, revisa tu escenario en el Simulador y en la vista previa del curso antes de publicarlo.",
        },
      ],
    },
  },
};

export default interactiveScenariosGuide;
